import Vue from 'vue'
import { $appHook } from '@shared/model/$appHook'
import { $AxE2AppHooks } from './$AxE2AppHooks'
import { $axAuthState } from '@/services/$axAuthStateManager'
import { $axFrontendHelper } from '../services/$axFrontendHelper'
import { $lsCache } from './$lsCache'
import { AxUserAddress } from '@/types/AxUserType'
import { AxOrderCommitter } from './AxOrderCommitter'
import { $toast } from './$toast'
import { GMOPGNewCardTokenObject, SavedCardObject } from '../models/creditCards'
import { axApiPaths } from '@/AxApiPaths'
import {
  CartDisplayItem,
  CartFields,
  CartItemEditParams,
  DELIVERY_TYPE,
  ITEM_TYPE,
  ItemCartSummary,
  ItemInCartResponse,
  PAYMENT_METHOD,
  PaymentMethodCd,
} from '@/types/carts'
import { CartItem } from '@/types/carts'
import { $AxeItems } from '@/models/axeItems'
import { RegularItemKbn } from '@/types/AxeItem'

export interface AxPaymentMethod {
  id: string // "xxx",
  code: string // "xxx",
  name: string // "xxxxxx",
  payment_fee: number // 330,
  payment_service_id: string // "1" // ,
  description: string // "xxx",
  is_sandbox: boolean // false,
  option: {
    [propName: string]: any
  }
}

export interface PaymentCreditCard {
  card_id: string
  card_no: string
  expire: string
  holder_name: string
  // token?: string,
  brand_name?: string
}

export const cartLsCacheKey = {
  RegularCourseCd: 'AxE2--cart-regularCourseCd',
  deliveryAddress: 'AxE2--cart-deliveryAddress',
  paymentCode: 'AxE2--cart-paymentCode',
  paymentCardData: 'AxE2--cart-paymentCardData',
  cartItems: 'AxE2--cart-cartItems',
  // cartCode: 'AxE2--cart-code',
}

export class AxE2Cart {
  temporary_order_id: string
  cartDetailIsLoading: boolean
  _cartItemsReactive: CartDisplayItem
  $orderCommitter: AxOrderCommitter
  _deliveryAddressReactive: AxUserAddress
  _totalPrice: number
  selectpaymentMethod: AxPaymentMethod
  _cartAccount: string

  constructor() {
    this.cartDetailIsLoading = false
    this.$orderCommitter = new AxOrderCommitter(this)
  }

  removeCartItems(): void {
    $lsCache.clear(cartLsCacheKey.cartItems)
  }

  /**
   * 定期コース getter/setter
   */
  set course_cd(num: number) {
    $lsCache.set(cartLsCacheKey.RegularCourseCd, num)
  }
  get course_cd(): number {
    const num = $lsCache.get(cartLsCacheKey.RegularCourseCd)
    return num || 1
  }
  removeRegularCourseCd(): void {
    $lsCache.clear(cartLsCacheKey.RegularCourseCd)
  }

  set cart_code(str: string) {
    $axAuthState.cart_code = str
  }

  get cart_code() {
    return $axAuthState.cart_code
  }

  // カート表示情報取得
  async getDisplayCartItems(): Promise<CartDisplayItem> {
    // カート情報取得
    const items = await this.getCartFromApi()
    console.log('[getDisplayCartItems] items: ', items)

    Vue.set(this, '_cartItemsReactive', items)
    return items
  }

  /**
   * カート表示内容をAPIから取得
   */
  async getCartFromApi(): Promise<CartDisplayItem> {
    try {
      const res = await $axFrontendHelper.requestToNormal({
        params: { axApiPath: axApiPaths.carts.display.get },
        path: axApiPaths.carts.display.get,
        method: 'POST',
        data: {
          cart_code: this.cart_code,
        },
      })
      console.log('[getCartFromApi] res: ', res.cart)
      return res.cart
    } catch (e) {
      //$toast.error('カート取得に失敗しました。')
      console.log('cart get failed')
      return null
    }
  }

  /**
   * カートへ追加
   * @param item
   */
  async addToCart(item: CartItemEditParams) {
    const result = await this._addToCarts(item)
    await this.cleanupState()
    return result
  }

  /**
   * カートから削除
   */
  async removeFromCart(itemSkuCode: string) {
    // 定期購入者値引きのリセット処理が必要なのでAPI側で処理
    try {
      const res = await $axFrontendHelper.requestToNormal({
        params: { axApiPath: axApiPaths.carts.remove },
        path: axApiPaths.carts.remove,
        method: 'POST',
        data: {
          cart_code: this.cart_code,
          itemSkuCode: itemSkuCode,
        },
      })
      return true
    } catch (e) {
      $toast.error('カートからの削除に失敗しました。')
      return false
    }
  }

  /**
   * カート内の特定商品の数量を変更
   * @param item
   * @param order_quantity
   */
  async setCartItemQuantity(itemSkuCode: string, order_quantity: number) {
    // console.log(typeof order_quantity)
    const result = await this._putToCart([
      {
        'item.skus.code': itemSkuCode,
        order_quantity,
      },
    ])
    return result
  }

  /**
   * 商品の定期⇔通常コース変更
   * @param itemSkuCode 対象商品のSKUコード
   * @param quantity 対象商品の数量
   * @param teikiFlg 変更後の定期区分
   * @returns
   */
  async changeItemType(itemSkuCode: string, quantity: number, teikiFlg: boolean) {
    const itemType = teikiFlg ? RegularItemKbn.Regular : RegularItemKbn.Normal
    const beforeItem = await $AxeItems.getItemByItemDetailId(itemSkuCode)

    // 現状と変更後で通常定期区分が同じなら変更しない
    if (beforeItem.regularItemKbn === itemType) {
      return false
    }

    // 通常・定期 探す対象によって変更後の探し方を変える。
    let afterItem = null
    if (teikiFlg) {
      // 定期商品を探す
      // 現在の商品のchangeRegularItemIdが指定する商品を持ってくる。
      afterItem = await $AxeItems.all.find((item) => item.itemDetailId === beforeItem.changeRegularItemId)
    } else {
      // 通常商品を探す
      // 現在の商品のコードをregularItemDetailIdのいずれかに持っている商品を持ってくる。
      afterItem = await $AxeItems.all.find(
        (item) =>
          item.regularItemDetailId['1'] === itemSkuCode ||
          item.regularItemDetailId['2'] === itemSkuCode ||
          item.regularItemDetailId['3'] === itemSkuCode
      )
    }

    if (afterItem) {
      // 定期購入者減額チェックに影響があるため先に変更前商品をカートから削除
      await this.removeFromCart(itemSkuCode)
      if (!(await this.addToCart({ 'item.skus.code': String(afterItem.itemDetailId), order_quantity: quantity }))) {
        // カート追加に失敗した場合は削除した商品を戻す
        await this._putToCart([{ 'item.skus.code': String(itemSkuCode), order_quantity: quantity }])
      }
      // location.reload()
      return
    }
  }

  /**
   * 定期コース種別変更
   */
  async changeRegularCourse(regularCourseCd: number) {
    try {
      if (this.course_cd === regularCourseCd) {
        return true
      }
      const cart = await this.getCartFromApi()
      const regularItems = cart.regularCartItemData.items
      // カート内の定期商品を対象コースの商品に入替え
      const editRegularItems: CartItemEditParams[] = []
      await regularItems.map(async (item) => {
        // カートに入っている定期商品を削除
        await editRegularItems.push({ 'item.skus.code': item.itemSkuCode, order_quantity: 0 })
        // コースに対応した定期商品のSKUコードを追加
        const normalItem = await $AxeItems.getNormalItemByRegularItemDetailId(item.itemSkuCode)
        await editRegularItems.push({
          'item.skus.code': String(normalItem.regularItemDetailId[regularCourseCd]),
          // 数量がコースの下限数に満たない場合は下限数を設定
          order_quantity: Math.max(item.quantity, regularCourseCd),
        })
      })
      console.log('[changeRegularCourse] editRegularItems: ', editRegularItems)
      await this.sleep(1.5)
      if (editRegularItems.length > 0) {
        // カート編集API呼出
        const res = await this._putToCart(editRegularItems)
        console.log('[changeRegularCourse] _putToCart res: ', res)
      }

      // セッションに保存
      this.course_cd = regularCourseCd
      return true
    } catch (e) {
      console.log('[changeRegularCourse] error:', e)
      return false
    }
  }

  /**
   * カートの通常商品を全て定期コースに変更
   */
  async changeAllNormalItemsToRegular() {
    await Promise.all(
      this._cartItemsReactive.standardCartItemData.items.map(async (item) => {
        if (!item.isTrial) {
          await this.changeItemType(item.itemSkuCode, item.quantity, true)
        }
      })
    )
    await this.cleanupState()
    await location.reload()
  }

  /**
   * 現在のカートの状態をクリアする
   */
  async cleanupState() {
    await this.resetCart()
    await this.removePaymentCreditCard()
    await this.removeDeliveryAddress()
    await this.$orderCommitter._cleanupOrderCaches()
    this.cart_code = null
  }

  /**
   * カート取得API呼び出し（カート取得）
   * @param lines 登録対象のカート内容
   * @private
   */
  async _getFromCart(): Promise<CartItem[]> {
    try {
      const res = await $axFrontendHelper.request({
        params: { axApiPath: axApiPaths.carts.get },
        path: axApiPaths.carts.get,
        data: {
          cart_code: this.cart_code,
          query: {},
          request: {
            fields: CartFields,
          },
        },
      })
      return res.result.cart.lines
    } catch (e) {
      //$toast.error('カート取得に失敗しました。')
      console.log('cart get failed')
      return null
    }
  }

  /**
   * カート編集API呼び出し
   * @param cartItems 登録対象のカート内容
   * @param hooks? 通知が必要な場合Trueを設定
   * @returns
   * @private
   */
  async _putToCart(cartItems: CartItemEditParams[], hooks?: boolean): Promise<boolean> {
    console.log('[_putToCart] cartItems: ', cartItems)
    try {
      const res = await $axFrontendHelper.requestToNormal({
        params: { axApiPath: '/cart/put2' },
        path: '/carts/put2',
        method: 'POST',
        data: {
          query: {
            cart: {
              code: this.cart_code,
              lines: cartItems,
            },
          },
        },
      })
      console.log('[_putToCart] cartItems: ', cartItems)
      console.log('[_putToCart] res: ', res)

      // if (res.result.cart.code != "") {
      //   this.cart_code = res.result.cart.code
      //   console.log('[_putToCart] after code: ', res.result.cart.code)
      // }
      // if ('\"code\":\"010-007'
      return true
    } catch (e) {
      await $appHook.callHooks($AxE2AppHooks.cart.add.ERROR, cartItems, this)
      return false
    }
  }

  /**
   * カート追加API呼出し
   * @param item
   * @private
   */
  async _addToCarts(item: CartItemEditParams): Promise<boolean> {
    try {
      console.log('[_addToCarts] item: ', item)
      console.log('[_addToCarts] cart_code: ', this.cart_code)
      const axeItem = await $AxeItems.getItemByItemDetailId(item['item.skus.code'])
      item['item.code'] = String(axeItem.itemId)
      const res = await $axFrontendHelper.requestToNormal({
        params: { axApiPath: axApiPaths.carts.add },
        path: axApiPaths.carts.add,
        method: 'POST',
        data: {
          cart_code: this.cart_code,
          regularCourseCd: this.course_cd,
          cartItemEditParams: item,
        },
      })
      return true
    } catch (e) {
      await $appHook.callHooks($AxE2AppHooks.cart.add.ERROR)
      return false
    }
  }

  /**
   * お届け先getter/setter
   */
  set deliveryAddress(deliveryAddress: AxUserAddress) {
    $lsCache.set(cartLsCacheKey.deliveryAddress, deliveryAddress)
  }
  get deliveryAddress(): AxUserAddress {
    const deliveryAddress = $lsCache.get(cartLsCacheKey.deliveryAddress)
    return deliveryAddress || null
  }
  removeDeliveryAddress() {
    $lsCache.clear(cartLsCacheKey.deliveryAddress)
  }

  /**
   * お支払い方法getter/setter
   */
  set paymentMethod(paymentMethod: AxPaymentMethod) {
    $lsCache.set(cartLsCacheKey.paymentCode, paymentMethod?.code)
    this.selectpaymentMethod = paymentMethod
  }
  get paymentMethod(): AxPaymentMethod {
    return this.selectpaymentMethod
  }
  /**
   * お支払いカードgetter/setter
   */
  set paymentCreditCard(obj: PaymentCreditCard) {
    $lsCache.set(cartLsCacheKey.paymentCardData, obj)
  }
  get paymentCreditCard(): PaymentCreditCard {
    return $lsCache.get(cartLsCacheKey.paymentCardData)
  }
  removePaymentCreditCard(): void {
    $lsCache.clear(cartLsCacheKey.paymentCardData)
  }

  /**
   * クラウドECの支払方法コードをクラウドKの支払方法コードに変換
   */
  convertPaymentMethodsCd(code: string) {
    return Object.keys(PaymentMethodCd).find((key) => PaymentMethodCd[key] === code)
  }

  /**
   * カート内アイテムがトライアル商品１つのみかどうかを判定
   * @returns
   */
  async isTrialOnly() {
    if (this._cartItemsReactive) {
      return (
        this._cartItemsReactive.standardCartItemData.items.length === 1 &&
        this._cartItemsReactive.regularCartItemData.items.length === 0 &&
        this._cartItemsReactive.standardCartItemData.items[0].isTrial
      )
    }
    return false
  }

  async removeAllItems() {
    // カート情報取得
    const cartItems = await this.getCartFromApi()
    let itemSkuCodes = []
    await cartItems.regularCartItemData.items.forEach(async (item) => await itemSkuCodes.push(item.itemSkuCode))
    await cartItems.standardCartItemData.items.forEach(async (item) => await itemSkuCodes.push(item.itemSkuCode))
    await itemSkuCodes.forEach(async (itemSkuCode) => await this.removeFromCart(itemSkuCode))
  }

  async removeAllTrialItems() {
    // カート情報取得
    const cartItems = await this.getCartFromApi()
    let itemSkuCodes = []
    await cartItems.standardCartItemData.items.forEach(async (item) => {
      if (item.isTrial) {
        await itemSkuCodes.push(item.itemSkuCode)
      }
    })
    await itemSkuCodes.forEach(async (itemSkuCode) => await this.removeFromCart(itemSkuCode))
  }

  /**
   *
   */
  async resetCart() {
    console.log('[resetCart] resetCart before code: ', this.cart_code)
    const res = await $axFrontendHelper.request({
      path: axApiPaths.carts.clear,
      data: {
        cart_codes: [this.cart_code],
      },
    })
    console.log('[resetCart]: res', res)
    return true
  }

  async sleep(second: number) {
    new Promise((resolve) => setTimeout(resolve, second * 1000))
  }
}

export const $cart = Vue.observable(new AxE2Cart())
